import { PlusCircle } from "lucide-react";
import { useState } from "react";
import { useForm } from "react-hook-form";

import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { useCongress } from "@/features/congress-select/hooks/use-congress";
import { queryClient } from "@/libs/react-query";
import { zodResolver } from "@hookform/resolvers/zod";
import { useTranslation } from "react-i18next";

import { useCreateNavigation } from "../api/create-navigation";
import { useNavigations } from "../api/get-navigations";
import {
  CreateNavigationFormData,
  createNavitationSchema,
} from "../types/create-navigation-schema";

function CreateNavigation() {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const { mutateAsync: create, isPending } = useCreateNavigation();
  const { congress } = useCongress();

  const { data, isLoading, isSuccess } = useNavigations(congress.id);

  const form = useForm<CreateNavigationFormData>({
    resolver: zodResolver(createNavitationSchema),
    defaultValues: {
      title: "",
      isSidebar: false,
    },
  });

  const sortedMenu = isSuccess
    ? [...data].sort((a, b) => b.order - a.order)
    : [];
  const highestOrder = sortedMenu.length > 0 ? sortedMenu[0].order : 0;

  //TODO: handle server error
  const onSubmit = async (values: CreateNavigationFormData) => {
    await create({
      requestBody: {
        title: values.title,
        congressId: congress.id,
        order: highestOrder + 1,
        type: "LIST",
        isSidebar: values.isSidebar,
      },
    });

    await queryClient.refetchQueries({
      queryKey: ["navigation"],
    });

    form.reset({
      title: "",
      isSidebar: false,
    });

    setOpen(false);
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button size="sm" type="button" disabled={isLoading}>
          <PlusCircle className="mr-1 size-3.5" />
          {t("navigationPage.addMenu")}
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t("navigationPage.addMenu")}</DialogTitle>
        </DialogHeader>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-8">
            <div className="space-y-3">
              <FormField
                control={form.control}
                name="title"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t("navigationPage.menuTitle")}</FormLabel>
                    <FormControl>
                      <Input
                        placeholder={t("navigationPage.menuTitle")}
                        className="h-8 px-2 py-1"
                        disabled={form.formState.isSubmitting}
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="isSidebar"
                render={({ field }) => (
                  <FormItem className="flex flex-row items-start space-x-3 space-y-0">
                    <FormControl>
                      <Checkbox
                        checked={field.value}
                        onCheckedChange={field.onChange}
                        disabled={field.disabled}
                      />
                    </FormControl>
                    <div className="space-y-1 leading-none">
                      <FormLabel>{t("navigationPage.isSidebar")}</FormLabel>
                    </div>
                  </FormItem>
                )}
              />

              <DialogFooter>
                <DialogClose asChild>
                  <Button variant="outline" type="button" disabled={isPending}>
                    {t("navigationPage.cancel")}
                  </Button>
                </DialogClose>
                <Button disabled={isPending}>{t("navigationPage.save")}</Button>
              </DialogFooter>
            </div>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}

export { CreateNavigation };
